import { z } from 'zod';

/**
 * Configuration des notifications Slack envoyées par l'API quand certains
 * events métier se produisent. Stockée dans `AppSetting('slack_notifications')`
 * sous forme de JSON (un seul record, global). Éditable côté admin via la
 * section dédiée dans /configuration.
 *
 * V1 = un seul canal cible via Incoming Webhook. Si on passe sur un Slack
 * Bot OAuth multi-canal en V2, ce schéma s'étendra avec un `channelId` ou
 * un mapping par partenaire/équipe.
 */
export const SlackNotificationEventsSchema = z.object({
  /** Notifie à chaque création de fiche (post-validation OTP). */
  leadCreated: z.boolean().default(true),
  /** Notifie à chaque changement de statut (created→scheduled, etc.). */
  statusChanged: z.boolean().default(true),
  /** Notifie à chaque qualification callcenter ingérée via Pub/Sub. */
  qualificationReceived: z.boolean().default(true),
  /** Notifie spécifiquement les passages en `delivery_failed`. Redondant
      avec `statusChanged` mais permet de couper l'un sans l'autre — ex.
      garder seulement les échecs si le canal devient trop bavard. */
  deliveryFailed: z.boolean().default(true),
  /** Notifie le cycle de vie des tickets lead (création vendeur, réponse
      admin, changement de statut). `.default(true)` = rétrocompat : les
      configs stockées avant l'ajout du toggle l'activent implicitement. */
  ticketActivity: z.boolean().default(true),
  /** Digest du watchdog : fiches dont le créneau d'injection est dépassé
      sans livraison, ou coincées en (cours de) livraison sans ack
      partenaire. UN message listant toutes les fiches en souffrance à
      chaque scan (Cloud Scheduler) — pas un event par fiche. */
  watchdogDigest: z.boolean().default(true),
});
export type SlackNotificationEvents = z.infer<typeof SlackNotificationEventsSchema>;

export const SlackNotificationsConfigSchema = z.object({
  enabled: z.boolean().default(false),
  /** Incoming Webhook URL fournie par Slack. Format
      `https://hooks.slack.com/services/T.../B.../...`. Stocké en clair
      dans le JSON `AppSetting.value` (cohérent avec `hoptiAccountId`),
      masqué côté UI via SensitiveField. */
  webhookUrl: z.string().url().or(z.literal('')).default(''),
  events: SlackNotificationEventsSchema.default({
    leadCreated: true,
    statusChanged: true,
    qualificationReceived: true,
    deliveryFailed: true,
    ticketActivity: true,
    watchdogDigest: true,
  }),
});
export type SlackNotificationsConfig = z.infer<typeof SlackNotificationsConfigSchema>;

/** Payload du PATCH /configuration/slack — partial pour permettre de ne
    toggler qu'un seul flag sans renvoyer tout le bloc. */
export const UpdateSlackNotificationsConfigSchema = z
  .object({
    enabled: z.boolean().optional(),
    webhookUrl: z.string().url().or(z.literal('')).optional(),
    events: SlackNotificationEventsSchema.partial().optional(),
  })
  .strict();
export type UpdateSlackNotificationsConfigInput = z.infer<
  typeof UpdateSlackNotificationsConfigSchema
>;
